import { ApButtonGroup, ApButtonIcon, ApModal } from '@alixpartners/ui-components';
import { ApButtonCasual, ApButtonMainEx } from 'components/UIComponents/Buttons';
import { DivWithLoader } from 'components/UIComponents/DivWithLoader/DivWithLoader';
import { useApiQuery } from 'hooks/useApiQuery';
import { SyntheticEvent, useMemo, useState } from 'react';
import { formatDate, getCurrentDate, US_DATE_FORMAT } from 'utils/formatDate';
import { apiSettlementSingle } from '../../api/apiSettlementSingle';
import { useSettlementSingleContext } from '../../dataProvider/SettlementSingleContextProvider';
import { DialogCustomEmail } from './DialogCustomEmail';
import './DialogEmailMessageNotification.css';

interface Props {
  onClose: () => void;
}

export const DialogEmailMessageNotification = ({ onClose }: Props) => {
  const { data, getSelectedCounterparty, clickButtonEmailNotification } =
    useSettlementSingleContext();

  const [customEmail, setCustomEmail] = useState('');
  const [showDialogCustomEmail, setShowDialogCustomEmail] = useState(false);

  const selectedClaimant = useMemo(() => {
    const user = getSelectedCounterparty();
    return {
      name: user?.label ?? '',
      email: user?.email ?? '',
      emailTo: user ? `${user.label} <${user.email}>` : '',
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const emailPreview = useApiQuery({
    queryFn: () =>
      apiSettlementSingle.getEmailPreview({
        date: formatDate(getCurrentDate(), US_DATE_FORMAT),
        claimantName: selectedClaimant.name,
        userName: selectedClaimant.email,
      }),
  });

  const handleOkClick = () => {
    clickButtonEmailNotification({
      send: true,
      customText: customEmail,
    });
    onClose();
  };

  const handleCloseClick = () => {
    clickButtonEmailNotification({ send: false });
    onClose();
  };

  return (
    <>
      <ApModal
        className="settlements-email-message-dialog"
        isDismissible={false}
        isFluid={true}
        isOpen={true}
        zIndex={1010}
        footer={() => (
          <ApButtonGroup justifyContent="space-between">
            <ApButtonCasual onClick={() => setShowDialogCustomEmail(true)}>
              Add a personal message
            </ApButtonCasual>
            <ApButtonMainEx onClick={handleOkClick}>Send Email</ApButtonMainEx>
          </ApButtonGroup>
        )}
      >
        <div className="settlements-email-message">
          <div className="settlements-email-message__title">
            <div className="settlements-email-message__title-text">
              You are about to send an email, containing the{' '}
              <b>{data?.agreementFileName}</b> agreement for review, to{' '}
              <b>{selectedClaimant.emailTo}</b>.
            </div>
            <ApButtonIcon
              iconName="baseline_close"
              iconProps={{ iconSize: 30 }}
              onClick={handleCloseClick}
            />
          </div>
          <div className="settlements-email-message__email-preview">
            Email Preview
          </div>
          <DivWithLoader
            loading={emailPreview.loading}
            className="settlements-email-message__body thin-scrollbar"
          >
            <iframe
              id="settlements-email-preview"
              title="settlements email preview"
              className="settlements-email-message__body-frame"
              frameBorder="0"
              scrolling="no"
              height="800"
              width="100%"
              srcDoc={emailPreview.data}
              onLoad={resizeFrameToContentHeight}
            />
          </DivWithLoader>
        </div>
      </ApModal>

      {showDialogCustomEmail && (
        <DialogCustomEmail
          initialText={customEmail}
          senderEmailAddress={selectedClaimant.emailTo}
          onClose={() => setShowDialogCustomEmail(false)}
          onConfirm={setCustomEmail}
        />
      )}
    </>
  );
};

/*** A workaround function to fit the content of an iframe to its height */
const resizeFrameToContentHeight = (f: SyntheticEvent) => {
  const iframe = f.target as HTMLIFrameElement;
  if (!iframe || !iframe?.contentWindow) return;

  const FRAME_FOOTER = 100; // just for cosmetic purpose
  const contentHeight =
    iframe.contentWindow.document.body.scrollHeight + FRAME_FOOTER;

  iframe.height = `${contentHeight}`;
};
